import { type MuiTelInputCountry } from 'mui-tel-input';

export interface CountryConfig {
  name: string;
  code: MuiTelInputCountry;
  prefix: string;
  flag: string;
}

export interface Country {
  [key: string]: CountryConfig;
}

export const supportedCountries: Country = {
  LU: {
    name: 'Luxembourg',
    code: 'LU',
    prefix: '+352',
    flag: '/flags/lu.svg',
  },
  US: {
    name: 'United States',
    code: 'US',
    prefix: '+1',
    flag: '/flags/us.svg',
  },
  PH: {
    name: 'Philippines',
    code: 'PH',
    prefix: '+63',
    flag: '/flags/ph.svg',
  },
  BE: {
    name: 'Belgium',
    code: 'BE',
    prefix: '+32',
    flag: '/flags/be.svg',
  },
  // Additional countries that can be easily enabled in the future
  CA: {
    name: 'Canada',
    code: 'CA',
    prefix: '+1',
    flag: '/flags/ca.svg',
  },
  GB: {
    name: 'United Kingdom',
    code: 'GB',
    prefix: '+44',
    flag: '/flags/gb.svg',
  },
  DE: {
    name: 'Germany',
    code: 'DE',
    prefix: '+49',
    flag: '/flags/de.svg',
  },
  FR: {
    name: 'France',
    code: 'FR',
    prefix: '+33',
    flag: '/flags/fr.svg',
  },
  ES: {
    name: 'Spain',
    code: 'ES',
    prefix: '+34',
    flag: '/flags/es.svg',
  },
  IT: {
    name: 'Italy',
    code: 'IT',
    prefix: '+39',
    flag: '/flags/it.svg',
  },
};

// Configuration for which countries are enabled based on feature flags
// This makes it very easy to add/remove countries or create new feature flag combinations
export const countryConfiguration = {
  // Base countries (always enabled): Luxembourg, Italy, Belgium, France
  base: ['LU', 'IT', 'BE', 'FR'],
  // Additional countries when US_FI_PHONE_NUMBERS_SUPPORT is enabled: US, Philippines
  usfi_support: ['US', 'PH'],
};

export const getEnabledCountries = (hasUSFISupport: boolean): Country => {
  const enabledCountryCodes = hasUSFISupport
    ? [...countryConfiguration.base, ...countryConfiguration.usfi_support]
    : countryConfiguration.base;

  return enabledCountryCodes.reduce((acc, countryCode) => {
    if (supportedCountries[countryCode]) {
      acc[countryCode] = supportedCountries[countryCode];
    }
    return acc;
  }, {} as Country);
};

// Utility function to get all enabled prefixes
export const getEnabledPrefixes = (hasUSFISupport: boolean): string[] => {
  const enabledCountries = getEnabledCountries(hasUSFISupport);
  return Object.values(enabledCountries).map(country => country.prefix);
};

// Utility function to validate if a phone number starts with any enabled prefix
export const isValidPhoneNumberPrefix = (
  phoneNumber: string,
  hasUSFISupport: boolean
): boolean => {
  if (!phoneNumber || phoneNumber === '+' || phoneNumber === '') return true;

  const enabledPrefixes = getEnabledPrefixes(hasUSFISupport);
  return enabledPrefixes.some(prefix => phoneNumber.startsWith(prefix));
};

// Utility function to get the appropriate translation key for unsupported phone numbers
export const getUnsupportedPhoneNumberTranslationKey = (
  hasUSFISupport: boolean
): string => {
  if (hasUSFISupport) {
    return 'Only Luxembourg (+352), Italy (+39), Belgium (+32), France (+33), United States (+1), and Philippines (+63) phone numbers are supported';
  } else {
    return 'Only Luxembourg (+352), Italy (+39), Belgium (+32), and France (+33) phone numbers are supported';
  }
};

// Utility function to get the default initial value for phone input
export const getDefaultPhoneInitialValue = (
  hasUSFISupport: boolean
): string => {
  const enabledCountryCodes = hasUSFISupport
    ? [...countryConfiguration.base, ...countryConfiguration.usfi_support]
    : countryConfiguration.base;
  // When multiple countries are supported, leave empty so user can choose
  return enabledCountryCodes.length === 1
    ? supportedCountries[enabledCountryCodes[0]].prefix
    : '';
};

// Example function to show how easy it would be to add support for additional feature flags
// This is not used yet, but shows the extensibility of the system
export const getEnabledCountriesWithMultipleFlags = (
  hasUSFISupport: boolean,
  hasEuropeSupport = false,
  hasNorthAmericaSupport = false
): Country => {
  let enabledCountryCodes = [...countryConfiguration.base];

  if (hasUSFISupport) {
    enabledCountryCodes = [
      ...enabledCountryCodes,
      ...countryConfiguration.usfi_support,
    ];
  }

  // Future feature flags can be easily added here
  // if (hasEuropeSupport) {
  //   enabledCountryCodes = [...enabledCountryCodes, ...countryConfiguration.europe_support];
  // }
  // if (hasNorthAmericaSupport) {
  //   enabledCountryCodes = [...enabledCountryCodes, ...countryConfiguration.north_america_support];
  // }

  return enabledCountryCodes.reduce((acc, countryCode) => {
    if (supportedCountries[countryCode]) {
      acc[countryCode] = supportedCountries[countryCode];
    }
    return acc;
  }, {} as Country);
};
